import React from "react";
import { Autocomplete, useFilter } from "react-aria-components";

import { ListBox, ListBoxItem } from "@/components/ui/list-box";
import { SearchField, SearchInput } from "@/components/ui/search-field";
import { Text } from "@/components/ui/text";

type ExampleItem = {
  id: string;
  question: string;
};

type InsightsExamplesListProps = {
  examples: readonly string[];
  onSelect: (example: string) => void;
  isDisabled?: boolean;
  maxListHeightClassName?: string;
};

export function InsightsExamplesList({
  examples,
  onSelect,
  isDisabled = false,
  maxListHeightClassName = "max-h-52",
}: InsightsExamplesListProps) {
  const { contains } = useFilter({ sensitivity: "base" });
  const items = React.useMemo<ExampleItem[]>(
    () => examples.map((question) => ({ id: question, question })),
    [examples],
  );

  if (examples.length === 0) return null;

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border bg-muted/10">
      <div className="border-b bg-muted/20 px-3 py-2">
        <Text size="2" className="font-medium text-muted-foreground">
          Examples
        </Text>
      </div>
      <div className="flex min-h-0 flex-col">
        <Autocomplete filter={contains}>
          <SearchField
            aria-label="Filter examples"
            className="border-b"
            isDisabled={isDisabled}
            isInvalid={false}
          >
            <SearchInput
              placeholder="Filter examples…"
              className="rounded-none border-0 border-input text-xs shadow-none outline-none ring-0 focus:border-input focus:ring-0 focus:invalid:border-input focus:invalid:ring-0 invalid:border-input invalid:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </SearchField>
          <ListBox
            items={items}
            aria-label="Example questions"
            selectionMode="none"
            onAction={(key) => {
              if (isDisabled) return;
              onSelect(String(key));
            }}
            className={`${maxListHeightClassName} overflow-auto rounded-none p-0 outline-none`}
            shouldFocusOnHover
            renderEmptyState={() => (
              <Text size="2" muted className="px-2 py-2">
                No examples match.
              </Text>
            )}
          >
            {(item) => (
              <ListBoxItem id={item.id} textValue={item.question}>
                <span className="block w-full text-start text-xs leading-snug line-clamp-2">
                  {item.question}
                </span>
              </ListBoxItem>
            )}
          </ListBox>
        </Autocomplete>
      </div>
    </div>
  );
}
